import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CalendarIcon,
  ClockIcon,
  WrenchScrewdriverIcon,
  CheckCircleIcon,
  XCircleIcon,
  TruckIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { ServiceClient } from '@/types';
import { createAppointment, checkAvailability, AvailabilityResponse, getAvailableTimeSlots, TimeSlot, getActiveAppointment, ActiveAppointmentResponse, updateAppointment, formatAppointmentDate } from '@/services/appointments';
import { Vehicle, getVehicles } from '@/services/vehicles';
import HolidayCalendar from '@/components/HolidayCalendar';
import VehicleManager from '@/components/VehicleManager';

interface LocationState {
  selectedService?: ServiceClient;
  selectedServices?: ServiceClient[];
}

const BookAppointmentPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  
  // Estado para manejar múltiples servicios seleccionados
  const [selectedServices, setSelectedServices] = useState<ServiceClient[]>(
    state?.selectedServices || (state?.selectedService ? [state.selectedService] : [])
  );
  const [appointmentDate, setAppointmentDate] = useState('');
  const [appointmentTime, setAppointmentTime] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [availableTimeSlots, setAvailableTimeSlots] = useState<TimeSlot[]>([]);
  const [isLoadingTimeSlots, setIsLoadingTimeSlots] = useState(false);
  const [activeAppointmentData, setActiveAppointmentData] = useState<ActiveAppointmentResponse | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // 🔒 Flag para prevenir múltiples llamadas simultáneas
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitRef = useRef(false);

  // Cargar vehículos y verificar cita activa al montar el componente
  useEffect(() => {
    loadVehicles();
    checkActiveAppointment();
  }, []);

  // Cargar horarios disponibles cuando cambie la fecha
  useEffect(() => {
    if (appointmentDate) {
      loadAvailableTimeSlots();
    } else {
      setAvailableTimeSlots([]);
    }
  }, [appointmentDate]);

  const loadVehicles = async () => {
    try {
      const vehiclesData = await getVehicles();
      setVehicles(vehiclesData);

      // Seleccionar automáticamente el vehículo predeterminado
      const defaultVehicle = vehiclesData.find(v => v.isDefault);
      if (defaultVehicle) {
        setSelectedVehicle(defaultVehicle);
      }
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const checkActiveAppointment = async () => {
    try {
      const activeData = await getActiveAppointment();
      setActiveAppointmentData(activeData);

      if (activeData.hasActiveAppointment && activeData.appointment) {
        setIsEditMode(true);

        // Pre-llenar formulario con datos de la cita existente
        const appointment = activeData.appointment;
        // Extraer fecha y hora directamente del string ISO para evitar conversiones de timezone
        const dateTimeParts = appointment.appointmentDate.split('T');
        const datePart = dateTimeParts[0]; // YYYY-MM-DD
        const timePart = dateTimeParts[1].substring(0, 5); // HH:MM

        setAppointmentDate(datePart);
        setAppointmentTime(timePart);
        setNotes(appointment.notes || '');

        // Si hay servicio seleccionado desde navegación, mantenerlo, sino usar el de la cita
        if (selectedServices.length === 0) {
          setSelectedServices(appointment.services.map(service => ({
            id: service.id.toString(),
            name: service.name,
            shortDescription: '',
            price: service.price,
            estimatedTime: `${service.duration} min`
          })));
        }
      }
    } catch (error) {
      console.error('Error checking active appointment:', error);
      // Si hay error, continuar con flujo normal
      setActiveAppointmentData({ hasActiveAppointment: false });
    }
  };

  // Cargar horarios disponibles para la fecha seleccionada
  const loadAvailableTimeSlots = async () => {
    if (!appointmentDate) return;

    setIsLoadingTimeSlots(true);
    try {
      const timeSlotsData = await getAvailableTimeSlots(appointmentDate);
      // Solo mostrar los horarios que están disponibles
      const availableSlots = timeSlotsData.availableSlots.filter(slot => slot.available);
      setAvailableTimeSlots(availableSlots);

      // Si el horario seleccionado ya no está disponible, limpiarlo
      if (appointmentTime && !availableSlots.some(slot => slot.time === appointmentTime)) {
        setAppointmentTime('');
      }
    } catch (error) {
      console.error('Error loading available time slots:', error);
      setAvailableTimeSlots([]);
    } finally {
      setIsLoadingTimeSlots(false);
    }
  };

  // Generar fechas disponibles (próximos 30 días)
  const generateAvailableDates = () => {
    const dates = [];
    const today = new Date();
    
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      
      // Solo días laborables (lunes a viernes)
      if (date.getDay() >= 1 && date.getDay() <= 5) {
        dates.push({
          value: date.toISOString().split('T')[0],
          label: date.toLocaleDateString('es-ES', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: 'America/Santo_Domingo'
          })
        });
      }
    }
    
    return dates;
  };

  // Función para convertir formato 24h a 12h
  const formatTimeTo12Hour = (time24: string) => {
    const [hours, minutes] = time24.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('🚀 handleSubmit called with:', {
      selectedServices: selectedServices.length,
      serviceIds: selectedServices.map(s => s.id),
      isLoading,
      appointmentDate,
      appointmentTime
    });

    // ⚠️ PROTECCIÓN CRÍTICA: Evitar múltiples envíos
    if (isLoading || isSubmitting || submitRef.current) {
      console.log('🛑 Prevented duplicate submission - already processing', {
        isLoading,
        isSubmitting,
        submitRef: submitRef.current
      });
      return;
    }

    if (!appointmentDate || !appointmentTime) {
      setError('Por favor selecciona fecha y hora');
      return;
    }

    if (!isEditMode && selectedServices.length === 0) {
      setError('Por favor selecciona al menos un servicio');
      return;
    }

    if (!selectedVehicle) {
      setError('Por favor selecciona un vehículo');
      return;
    }

    console.log('⏳ Setting flags to prevent duplicate calls');
    setIsLoading(true);
    setIsSubmitting(true);
    submitRef.current = true;
    setError('');

    try {
      // Formato básico para que el servidor maneje el timezone
      const cleanTime = appointmentTime.split(':').slice(0, 2).join(':');
      const appointmentDateTime = `${appointmentDate}T${cleanTime}:00`;

      if (isEditMode && activeAppointmentData?.appointment) {
        // Modo edición: actualizar cita existente
        await updateAppointment(activeAppointmentData.appointment.id, {
          appointmentDate: appointmentDateTime,
          notes: notes.trim() || undefined
        });
      } else {
        // Modo creación: crear nueva cita
        await createAppointment({
          serviceIds: selectedServices.map(service => parseInt(service.id)),
          appointmentDate: appointmentDateTime,
          notes: notes.trim() || undefined,
          vehicleId: selectedVehicle.id
        });
      }

      setSuccess(true);

      // Redirigir después de 2 segundos
      setTimeout(() => {
        navigate('/appointments');
      }, 2000);

    } catch (err) {
      console.error('Error saving appointment:', err);
      if (err instanceof Error && err.message.includes('Ya tienes una cita activa')) {
        setError('Ya tienes una cita activa programada. Ve a "Mis Citas" para modificarla.');
      } else {
        setError(err instanceof Error ? err.message : (isEditMode ? 'Error al actualizar la cita' : 'Error al crear la cita'));
      }
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
      submitRef.current = false;
    }
  };

  const handleBackToServices = () => {
    navigate('/services', {
      state: {
        returnToBooking: true,
        selectedServices: selectedServices
      }
    });
  };

  const handleRemoveService = (serviceToRemove: ServiceClient) => {
    if (isEditMode) return; // No permitir remover servicios en modo edición

    setSelectedServices(prev =>
      prev.filter(service => service.id !== serviceToRemove.id)
    );
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <CheckCircleIcon className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isEditMode ? '¡Cita Actualizada!' : '¡Cita Reservada!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {isEditMode
              ? 'Tu cita ha sido actualizada exitosamente. Te redirigiremos a tus citas en unos segundos.'
              : 'Tu cita ha sido reservada exitosamente. Te redirigiremos a tus citas en unos segundos.'
            }
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-4">
            <CalendarIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isEditMode ? 'Modificar Cita' : 'Reservar Cita'}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? 'Modifica la fecha y hora de tu cita existente' : 'Selecciona la fecha y hora para tu servicio'}
          </p>
        </div>

        {/* Información de cita existente (solo en modo edición) */}
        {isEditMode && activeAppointmentData?.appointment && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mb-6">
            <h3 className="text-lg font-semibold text-yellow-900 mb-4">
              📝 Modificando Cita Existente
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium text-yellow-800">Servicios:</p>
                <p className="text-yellow-700">
                  {activeAppointmentData.appointment.services.map(s => s.name).join(', ')}
                </p>
              </div>
              <div>
                <p className="font-medium text-yellow-800">Fecha y hora actual:</p>
                <p className="text-yellow-700">{formatAppointmentDate(activeAppointmentData.appointment.appointmentDate)}</p>
              </div>
              <div>
                <p className="font-medium text-yellow-800">Estado:</p>
                <p className="text-yellow-700">{activeAppointmentData.appointment.status === 'scheduled' ? 'Programada' : activeAppointmentData.appointment.status}</p>
              </div>
              <div>
                <p className="font-medium text-yellow-800">Precio:</p>
                <p className="text-yellow-700">${activeAppointmentData.appointment.totalPrice}</p>
              </div>
            </div>
            <p className="text-yellow-700 text-xs mt-3">
              Puedes cambiar la fecha, hora y notas de tu cita. El servicio no puede ser modificado.
            </p>
          </div>
        )}

        {/* Formulario */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Servicios Seleccionados */}
            {(selectedServices.length > 0 || (isEditMode && activeAppointmentData?.appointment)) ? (
              <div className="bg-blue-50 rounded-lg p-4">
                <h3 className="font-semibold text-blue-900 mb-2">
                  {isEditMode ? 'Servicios de la Cita' : 'Servicios Seleccionados'}
                  {isEditMode && <span className="text-sm font-normal text-blue-700 ml-2">(No modificables)</span>}
                </h3>
                <div className="space-y-3">
                  {(isEditMode && activeAppointmentData?.appointment
                    ? activeAppointmentData.appointment.services
                    : selectedServices
                  ).map((service, index) => (
                    <div key={index} className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <WrenchScrewdriverIcon className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">
                          {service.name}
                        </h4>
                        <div className="flex items-center space-x-4 mt-1">
                          <span className="text-sm font-medium text-blue-600">
                            ${service.price}
                          </span>
                          <span className="text-sm text-gray-500">
                            <ClockIcon className="w-4 h-4 inline mr-1" />
                            {isEditMode && activeAppointmentData?.appointment
                              ? `${service.duration} min`
                              : service.estimatedTime}
                          </span>
                        </div>
                      </div>
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => handleRemoveService(service)}
                          className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remover servicio"
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                  {/* Total */}
                  <div className="border-t border-blue-200 pt-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-blue-900">Total:</span>
                      <div className="text-right">
                        <div className="font-bold text-blue-900">
                          ${isEditMode && activeAppointmentData?.appointment
                            ? activeAppointmentData.appointment.totalPrice
                            : selectedServices.reduce((sum, service) => sum + service.price, 0)}
                        </div>
                        <div className="text-sm text-blue-700">
                          {isEditMode && activeAppointmentData?.appointment
                            ? `${activeAppointmentData.appointment.totalDuration} min`
                            : `${selectedServices.reduce((sum, service) => {
                                const duration = parseInt(service.estimatedTime.replace(' min', ''));
                                return sum + duration;
                              }, 0)} min`}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {!isEditMode && (
                  <div className="mt-4">
                    <button
                      type="button"
                      onClick={handleBackToServices}
                      className="w-full py-2 px-4 bg-blue-100 text-blue-700 hover:bg-blue-200 rounded-lg transition-colors text-sm font-medium"
                    >
                      + Agregar más servicios
                    </button>
                  </div>
                )}
              </div>
            ) : !isEditMode ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-yellow-800 text-sm">
                  No se han seleccionado servicios.
                  <button
                    type="button"
                    onClick={handleBackToServices}
                    className="text-yellow-600 hover:text-yellow-700 font-medium ml-1"
                  >
                    Seleccionar servicios
                  </button>
                </p>
              </div>
            ) : null}

            {/* Selección de Vehículo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehículo para el servicio
              </label>
              {vehicles.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <TruckIcon className="w-5 h-5 text-yellow-600 mr-2" />
                    <div>
                      <p className="text-yellow-800 text-sm font-medium">
                        No tienes vehículos registrados
                      </p>
                      <p className="text-yellow-700 text-xs mt-1">
                        Necesitas agregar un vehículo para continuar con la reserva
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {vehicles.map((vehicle) => (
                    <div
                      key={vehicle.id}
                      className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                        selectedVehicle?.id === vehicle.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      onClick={() => setSelectedVehicle(vehicle)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <TruckIcon className="w-5 h-5 text-gray-600" />
                          <div>
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-gray-900">
                                {vehicle.brand} {vehicle.model}
                              </span>
                              {vehicle.isDefault && (
                                <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                                  Predeterminado
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              {vehicle.year} • {vehicle.plate}
                            </p>
                          </div>
                        </div>
                        {selectedVehicle?.id === vehicle.id && (
                          <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Fecha */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <CalendarIcon className="w-5 h-5 inline mr-2" />
                Fecha de la cita
              </label>
              <HolidayCalendar
                selectedDate={appointmentDate}
                onDateSelect={setAppointmentDate}
                minDate={new Date().toISOString().split('T')[0]}
                className="max-w-md mx-auto"
              />
            </div>

            {/* Hora */}
            <div>
              <label htmlFor="appointmentTime" className="block text-sm font-medium text-gray-700 mb-2">
                <ClockIcon className="w-5 h-5 inline mr-2" />
                Hora de la cita
                {isLoadingTimeSlots && (
                  <span className="text-sm text-gray-500 ml-2">
                    (Cargando horarios disponibles...)
                  </span>
                )}
              </label>

              {appointmentDate ? (
                isLoadingTimeSlots ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-2"></div>
                    <p className="text-gray-500">Cargando horarios disponibles...</p>
                  </div>
                ) : availableTimeSlots.length > 0 ? (
                  <div className="space-y-2">
                    {availableTimeSlots.map((timeSlot) => {
                      const isSelected = appointmentTime === timeSlot.time;

                      return (
                        <div
                          key={timeSlot.time}
                          className={`border-2 rounded-lg p-3 cursor-pointer transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-green-200 bg-green-50 hover:border-green-300'
                          }`}
                          onClick={() => setAppointmentTime(timeSlot.time)}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <ClockIcon className="w-5 h-5 text-gray-600" />
                              <div>
                                <div className="flex items-center space-x-2">
                                  <span className="font-medium text-gray-900">
                                    {formatTimeTo12Hour(timeSlot.time)}
                                  </span>
                                  {isSelected && (
                                    <CheckCircleIcon className="w-5 h-5 text-blue-500" />
                                  )}
                                </div>
                                <div className="text-sm">
                                  <span className="text-green-600">
                                    ✓ Disponible ({timeSlot.remainingSlots}/{timeSlot.maxCapacity} cupos)
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800 text-sm">
                      No hay horarios disponibles para esta fecha. Por favor selecciona otra fecha.
                    </p>
                  </div>
                )
              ) : (
                <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-500">
                  Selecciona una fecha primero para ver los horarios disponibles
                </div>
              )}
            </div>

            {/* Notas */}
            <div>
              <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                Notas adicionales (opcional)
              </label>
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Información adicional sobre tu vehículo o el servicio..."
              />
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <div className="flex items-center">
                  <XCircleIcon className="w-5 h-5 text-red-400 mr-2" />
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}

            {/* Botones */}
            <div className="flex space-x-4">
              <button
                type="button"
                onClick={handleBackToServices}
                className="flex-1 btn-secondary"
              >
                Volver a Servicios
              </button>
              <button
                type="submit"
                disabled={
                  isLoading ||
                  isSubmitting ||
                  (!isEditMode && selectedServices.length === 0) ||
                  !selectedVehicle ||
                  !appointmentDate ||
                  !appointmentTime ||
                  isLoadingTimeSlots ||
                  availableTimeSlots.length === 0
                }
                onClick={(e) => {
                  if (isLoading || isSubmitting) {
                    console.log('🛑 Button click prevented - already processing', { isLoading, isSubmitting });
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                  }
                  console.log('✅ Button click allowed');
                }}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>{isEditMode ? 'Actualizando...' : 'Reservando...'}</span>
                  </div>
                ) : (
                  isEditMode ? 'Actualizar Cita' : 'Reservar Cita'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Información adicional */}
        <div className="mt-6 space-y-4">
          {/* Información sobre disponibilidad */}
          {appointmentDate && availableTimeSlots.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 mb-2">Información de Disponibilidad</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-green-700">Horarios disponibles</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <span className="text-blue-700">Horario seleccionado</span>
                </div>
              </div>
              <p className="text-blue-700 text-xs mt-2">
                Solo se muestran los horarios con cupos disponibles. Los horarios sin capacidad no aparecen en la lista.
              </p>
            </div>
          )}

          <div className="text-center">
            <p className="text-sm text-gray-500">
              Las citas pueden ser modificadas o canceladas hasta 24 horas antes de la fecha programada.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookAppointmentPage;
